/**
 * KeyboardController - Keyboard event handling and shortcuts
 * 
 * Provides:
 * - Key state tracking (which keys are currently held)
 * - Modifier key state (Alt, Ctrl, Shift, Meta)
 * - WASD direction key tracking for Alt+WASD shortcuts
 * - Keyboard shortcut registration and dispatch
 */

// ============= KEY STATE TRACKING =============

// Currently held keys (lowercase)
const heldKeys = new Set();

// WASD side mapping for port group shortcuts
const WASD_SIDE_MAP = {
    'w': 'top',
    'a': 'left',
    's': 'bottom',
    'd': 'right'
};

// Currently held WASD key (for Alt+WASD shortcuts)
let heldWasdKey = null;

// ============= REGISTERED SHORTCUTS =============

// Shortcut registry: Map<string, { handler, description, preventDefault }>
// Key format: "ctrl+z", "shift+delete", "escape", etc.
const shortcuts = new Map();

// Callback for when Alt key state changes (for multi-drag preview)
let onAltKeyChange = null;

// ============= INITIALIZATION =============

let initialized = false;

/**
 * Initialize the keyboard controller
 * @param {Object} options - Options
 * @param {Function} [options.onAltKeyChange] - Callback when Alt key is pressed/released
 */
export function initKeyboardController(options = {}) {
    if (initialized) return;
    
    onAltKeyChange = options.onAltKeyChange || null;
    
    // Track all key presses
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    
    // Clear state when window loses focus
    window.addEventListener('blur', clearAllKeys);
    
    initialized = true;
}

// ============= EVENT HANDLERS =============

function handleKeyDown(e) {
    const key = e.key.toLowerCase();
    heldKeys.add(key);
    
    // Track WASD keys
    if (WASD_SIDE_MAP[key]) {
        heldWasdKey = key;
    }
    
    // Alt key callback
    if (e.key === 'Alt' && onAltKeyChange) {
        onAltKeyChange(true, e);
    }
    
    // Check registered shortcuts (only if not in input field)
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'SELECT' && e.target.tagName !== 'TEXTAREA') {
        const shortcutKey = buildShortcutKey(e);
        const shortcut = shortcuts.get(shortcutKey);
        
        if (shortcut) {
            if (shortcut.preventDefault !== false) {
                e.preventDefault();
            }
            shortcut.handler(e);
        }
    }
}

function handleKeyUp(e) {
    const key = e.key.toLowerCase();
    heldKeys.delete(key);
    
    // Clear WASD if released
    if (key === heldWasdKey) {
        heldWasdKey = null;
    }
    
    // Alt key callback
    if (e.key === 'Alt' && onAltKeyChange) {
        onAltKeyChange(false, e);
    }
}

function clearAllKeys() {
    heldKeys.clear();
    heldWasdKey = null;
}

// ============= SHORTCUT KEY BUILDING =============

/**
 * Build a shortcut key string from an event
 * Format: "ctrl+shift+k", "meta+z", "escape", etc.
 */
function buildShortcutKey(e) {
    const parts = [];
    
    if (e.ctrlKey || e.metaKey) parts.push('mod'); // Use 'mod' for Ctrl/Cmd
    if (e.shiftKey) parts.push('shift');
    if (e.altKey) parts.push('alt');
    
    // Normalize key name
    let key = e.key.toLowerCase();
    if (key === ' ') key = 'space';
    
    parts.push(key);
    
    return parts.join('+');
}

/**
 * Normalize a shortcut string for registration
 * Handles variations like "Ctrl+Z", "cmd+z", "mod+z"
 */
function normalizeShortcutKey(shortcut) {
    return shortcut
        .toLowerCase()
        .replace(/ctrl|cmd|meta/, 'mod')
        .split('+')
        .sort((a, b) => {
            // Modifiers first, then key
            const order = { mod: 0, shift: 1, alt: 2 };
            return (order[a] ?? 3) - (order[b] ?? 3);
        })
        .join('+');
}

// ============= PUBLIC API =============

/**
 * Register a keyboard shortcut
 * @param {string} shortcut - Shortcut string like "mod+z", "shift+delete", "escape"
 * @param {Function} handler - Handler function
 * @param {Object} [options] - Options
 * @param {string} [options.description] - Human-readable description
 * @param {boolean} [options.preventDefault=true] - Whether to prevent default
 */
export function registerShortcut(shortcut, handler, options = {}) {
    const key = normalizeShortcutKey(shortcut);
    shortcuts.set(key, {
        handler,
        description: options.description || shortcut,
        preventDefault: options.preventDefault !== false
    });
}

/**
 * Unregister a keyboard shortcut
 * @param {string} shortcut - Shortcut string
 */
export function unregisterShortcut(shortcut) {
    const key = normalizeShortcutKey(shortcut);
    shortcuts.delete(key);
}

/**
 * Check if a key is currently held
 * @param {string} key - Key to check (lowercase)
 * @returns {boolean}
 */
export function isKeyHeld(key) {
    return heldKeys.has(key.toLowerCase());
}

/**
 * Check if Alt key is held
 * @returns {boolean}
 */
export function isAltHeld() {
    return heldKeys.has('alt');
}

/**
 * Check if Ctrl/Cmd (mod) key is held
 * @returns {boolean}
 */
export function isModHeld() {
    return heldKeys.has('control') || heldKeys.has('meta');
}

/**
 * Check if Shift key is held
 * @returns {boolean}
 */
export function isShiftHeld() {
    return heldKeys.has('shift');
}

/**
 * Get the currently held WASD key
 * @returns {string|null} - 'w', 'a', 's', 'd', or null
 */
export function getHeldWasdKey() {
    return heldWasdKey;
}

/**
 * Get the side direction for the held WASD key
 * @returns {string|null} - 'top', 'left', 'bottom', 'right', or null
 */
export function getHeldWasdSide() {
    return heldWasdKey ? WASD_SIDE_MAP[heldWasdKey] : null;
}

/**
 * Get all registered shortcuts
 * @returns {Array<{ key: string, description: string }>}
 */
export function getRegisteredShortcuts() {
    return Array.from(shortcuts.entries()).map(([key, { description }]) => ({
        key,
        description
    }));
}

/**
 * Clear all registered shortcuts
 */
export function clearShortcuts() {
    shortcuts.clear();
}

// ============= WASD SIDE MAP EXPORT =============
export { WASD_SIDE_MAP };
